import { useEffect, useState } from 'react';
import apiClient from '@lib/api/client';

// Safety & WorkSafeBC reference for residential framing / foundation carpentry.
// Plain-language summaries with OHS Regulation part references and links to the
// official sources. THE REGULATION GOVERNS — this page is a field reminder, not
// a substitute for it or for site-specific instruction.

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
);
const B = ({ children }: { children: React.ReactNode }) => (
  <strong className="text-gray-800">{children}</strong>
);
const Rule = ({ children }: { children: React.ReactNode }) => (
  <li className="text-sm text-gray-600 leading-relaxed flex gap-2">
    <span className="material-symbols-outlined text-base text-[#C77D3F] mt-0.5">check_circle</span>
    <span>{children}</span>
  </li>
);

interface Section { icon: string; title: string; ref: string; body: React.ReactNode }

const SECTIONS: Section[] = [
  {
    icon: 'gavel',
    title: 'Your rights on every site',
    ref: 'OHS Reg. 3.12–3.13 · Workers Compensation Act',
    body: (
      <ul className="space-y-2">
        <Rule>You have the <B>right to refuse unsafe work</B>. If you believe a task would create an undue hazard, stop, tell your supervisor, and don't resume until it's resolved. You cannot be punished for a good-faith refusal.</Rule>
        <Rule>You have the right to <B>know the hazards</B> of your work, to <B>proper training</B> before doing a task, and to <B>participate</B> in safety discussions.</Rule>
        <Rule><B>Report hazards immediately</B> — a missing guardrail, an open hole, a damaged cord. Tell the site supervisor or the office.</Rule>
      </ul>
    ),
  },
  {
    icon: 'falling',
    title: 'Fall protection — the #1 killer in our trade',
    ref: 'OHS Reg. Part 11',
    body: (
      <div className="space-y-2">
        <ul className="space-y-2">
          <Rule>Fall protection is required when you could fall <B>3 m (10 ft)</B> or more — guardrails first; harness + lifeline where guardrails aren't practical.</Rule>
          <Rule>A <B>written fall protection plan</B> is required on sites where workers are at height of <B>7.5 m (25 ft)</B> or more. Know the plan before you go up.</Rule>
          <Rule>Guard or cover <B>every floor and roof opening</B> — stairwells, skylight holes, window openings above drops. A sheet of ply over a hole must be secured and marked.</Rule>
          <Rule>Inspect your harness and lanyard before each use. Anchor points must hold the required load — ask if you're not sure what's been engineered.</Rule>
        </ul>
        <P>Framing floors and roofs is exactly where BC's serious falls happen. If the edge isn't guarded and you're not tied off — stop and fix it first.</P>
      </div>
    ),
  },
  {
    icon: 'stairs',
    title: 'Ladders & work platforms',
    ref: 'OHS Reg. Part 13',
    body: (
      <ul className="space-y-2">
        <Rule>Set extension ladders at about <B>1 out : 4 up</B>, secured, extending roughly <B>1 m (3 ft)</B> above the landing.</Rule>
        <Rule><B>Three-point contact</B> going up or down — tools go up on a hoist line or in a pouch, not in your hand.</Rule>
        <Rule>Don't work from the top two rungs. If the job needs both hands and force (sheeting, beam work), use a <B>platform or scaffold</B>, not a ladder.</Rule>
        <Rule>Scaffolds need full planking, guardrails at 3 m+, plumb/level setup, and a competent-person inspection before use.</Rule>
      </ul>
    ),
  },
  {
    icon: 'foundation',
    title: 'Excavations & foundations',
    ref: 'OHS Reg. Part 20 (20.78–20.96)',
    body: (
      <ul className="space-y-2">
        <Rule>Before anyone enters an excavation deeper than <B>1.2 m (4 ft)</B>, it must be <B>sloped, benched, or shored</B> — vertical clay walls kill fast and without warning.</Rule>
        <Rule>Keep the <B>spoil pile and materials at least 0.6 m (2 ft)</B> back from the edge; keep machines back further.</Rule>
        <Rule>There must be <B>safe access/egress</B> (ladder or ramp) close to where you're working in the trench.</Rule>
        <Rule><B>Locate underground utilities before digging</B> — BC 1 Call. Hand-dig near marked lines.</Rule>
        <Rule>Never work alone in an excavation, and never under a suspended load or bucket.</Rule>
      </ul>
    ),
  },
  {
    icon: 'construction',
    title: 'Tools — saws, nailers, powder-actuated',
    ref: 'OHS Reg. Part 12',
    body: (
      <ul className="space-y-2">
        <Rule><B>Guards stay on.</B> A pinned-back saw guard or bypassed nailer safety is how fingers and femoral arteries get found the hard way.</Rule>
        <Rule>Nail guns: <B>sequential trigger</B> where provided, finger off the trigger while moving, never point at yourself or others, disconnect air before clearing jams.</Rule>
        <Rule>Powder-actuated tools require <B>training/certification</B> — don't use one you haven't been trained on.</Rule>
        <Rule>Damaged cords, missing prongs, cracked plugs — tag it and pull it from service. GFCI protection outdoors and in wet areas.</Rule>
      </ul>
    ),
  },
  {
    icon: 'health_and_safety',
    title: 'PPE — what you wear on our sites',
    ref: 'OHS Reg. Part 8 · company policy',
    body: (
      <ul className="space-y-2">
        <Rule><B>CSA safety footwear</B> at all times on site. <B>Hard hat</B> whenever overhead work, craning, or anyone working above you.</Rule>
        <Rule><B>Eye protection</B> cutting, nailing, grinding, or working under others. <B>Hearing protection</B> for saws, planers, compressors, and any prolonged loud work.</Rule>
        <Rule>Gloves suited to the task; high-visibility vest when machines are moving or you're near traffic.</Rule>
        <Rule>PPE is the <B>last</B> line of defence — it doesn't replace guards, guardrails, or safe setup.</Rule>
      </ul>
    ),
  },
  {
    icon: 'masks',
    title: 'Dust, silica & WHMIS',
    ref: 'OHS Reg. Parts 5–6 · WHMIS 2015',
    body: (
      <ul className="space-y-2">
        <Rule>Cutting/grinding <B>concrete, stone, or fibre-cement</B> releases silica dust — it scars lungs permanently. Use wet-cutting or dust extraction plus a properly fitted respirator per the site's silica plan.</Rule>
        <Rule>Treat pressure-treated lumber offcuts and dust with respect — don't burn PT wood, wash before eating.</Rule>
        <Rule>Know where the <B>SDS sheets</B> are for adhesives, foams, and finishes; check the label before first use of any product.</Rule>
      </ul>
    ),
  },
  {
    icon: 'medical_services',
    title: 'First aid & when someone gets hurt',
    ref: 'OHS Reg. Part 3, Schedule 3-A',
    body: (
      <div className="space-y-2">
        <ul className="space-y-2">
          <Rule>Know <B>where the first aid kit is</B> and who the site's first aid attendant is — ask at the start of every new site.</Rule>
          <Rule><B>Every injury gets reported</B> to the supervisor/office the same day — even "just a scratch." It protects your WorkSafeBC claim if it worsens.</Rule>
          <Rule>Serious incidents: call <B>911</B> first, then the office. Don't disturb the scene of a serious incident except to help the injured.</Rule>
        </ul>
        <P>If you're hurt at work: get treatment, report it to us, and file with WorkSafeBC (teleclaim <B>1-888-967-5377</B>). Your recovery and wage-loss coverage depend on prompt reporting.</P>
      </div>
    ),
  },
  {
    icon: 'school',
    title: 'New & young workers',
    ref: 'OHS Reg. 3.22–3.25',
    body: (
      <ul className="space-y-2">
        <Rule>Every worker <B>new to a site</B> gets an orientation: site hazards, first aid, emergency plan, who supervises them — before starting work.</Rule>
        <Rule>New to a task? Say so. Being shown properly once beats an injury and beats guessing. Supervision is our job; asking is yours.</Rule>
      </ul>
    ),
  },
  {
    icon: 'thermostat',
    title: 'Weather, working alone & housekeeping',
    ref: 'OHS Reg. Parts 4 & 7',
    body: (
      <ul className="space-y-2">
        <Rule><B>Heat</B>: water, shade breaks, watch each other for confusion/dizziness. <B>Cold/rain</B>: slippery sheathing and mud change what's safe — slow down, re-assess.</Rule>
        <Rule><B>Working alone</B> requires a check-in procedure — if you're the last one on site, make sure someone knows and expects your check-in.</Rule>
        <Rule>Housekeeping is fall protection at ground level: <B>nails pulled or bent over</B>, offcuts cleared from walkways, cords and hoses out of paths, materials stacked stable.</Rule>
      </ul>
    ),
  },
  {
    icon: 'policy',
    title: 'OchrePoint site policies',
    ref: 'Company policy',
    body: (
      <ul className="space-y-2">
        <Rule><B>Zero impairment</B> — no alcohol or impairing substances on site, including the drive-out. Prescription meds that impair: tell your supervisor privately.</Rule>
        <Rule><B>Phones down</B> while cutting, nailing, on ladders, or when machines are moving. Music in one ear max where hearing the site matters.</Rule>
        <Rule>Report near-misses, not just injuries — a dropped sheet that missed someone is tomorrow's incident.</Rule>
        <Rule>Harassment and bullying are safety issues and are not tolerated — report to the office; WorkSafeBC policies apply.</Rule>
      </ul>
    ),
  },
];

const LINKS = [
  { label: 'WorkSafeBC — OHS Regulation (searchable)', url: 'https://www.worksafebc.com/en/law-policy/occupational-health-safety/searchable-ohs-regulation' },
  { label: 'WorkSafeBC — Construction industry', url: 'https://www.worksafebc.com/en/health-safety/industries/construction' },
  { label: 'WorkSafeBC — Report an injury (Teleclaim)', url: 'https://www.worksafebc.com/en/claims/report-workplace-injury-illness' },
  { label: 'BC 1 Call (before you dig)', url: 'https://www.bc1c.ca' },
];

export const PortalSafetyPage = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-4 pb-4">
      <div>
        <h1 className="text-xl font-bold text-[#2C1810]">Safety</h1>
        <p className="text-sm text-gray-500 mt-0.5">WorkSafeBC rules & our policies — framing, foundations, and everything around them.</p>
      </div>

      {/* Toolbox talks — read and sign off */}
      <ToolboxTalksSection />

      {/* Emergency strip */}
      <div className="bg-red-600 rounded-xl p-4 text-white">
        <p className="text-xs font-bold uppercase tracking-wide text-white/80">Emergency</p>
        <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
          <a href="tel:911" className="text-2xl font-bold">📞 911</a>
          <div className="text-right text-xs leading-tight">
            <a href="tel:18886217233" className="block font-semibold">WorkSafeBC line: 1-888-621-7233</a>
            <a href="tel:18889675377" className="block text-white/80">Injury claim: 1-888-967-5377</a>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
        <span className="material-symbols-outlined text-base">warning</span>
        These are field reminders. The OHS Regulation and your site-specific instructions govern — when in doubt, stop and ask.
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {SECTIONS.map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50"
            >
              <span className="material-symbols-outlined text-[#C77D3F] text-xl">{s.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-gray-900 text-sm">{s.title}</span>
                <span className="block text-[10px] text-gray-400">{s.ref}</span>
              </span>
              <span className={`material-symbols-outlined text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`}>expand_more</span>
            </button>
            {open === i && <div className="px-4 pb-4 border-t border-gray-100 pt-3">{s.body}</div>}
          </div>
        ))}
      </div>

      {/* Official sources */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#C77D3F] text-xl">link</span>
          Official sources
        </h2>
        <div className="space-y-1.5">
          {LINKS.map(l => (
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#C77D3F] active:opacity-70">
              <span className="material-symbols-outlined text-base">open_in_new</span>
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};


// ── Toolbox talks: read + sign off (ack log is the WorkSafeBC record) ───────
interface PortalTalk { id: number; title: string; body: string; talk_date: string; acked_at: string | null }
function ToolboxTalksSection() {
  const [talks, setTalks] = useState<PortalTalk[]>([]);
  const [open, setOpen] = useState<number | null>(null);

  const load = () =>
    apiClient.get('/safety/me/toolbox-talks').then(r => {
      const list = (r.data.data ?? []) as PortalTalk[];
      setTalks(list);
      const firstUnacked = list.find(t => !t.acked_at);
      if (firstUnacked) setOpen(firstUnacked.id);
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  const ack = async (id: number) => {
    setTalks(ts => ts.map(t => t.id === id ? { ...t, acked_at: new Date().toISOString() } : t));
    try { await apiClient.post(`/safety/me/toolbox-talks/${id}/ack`, {}); } catch { load(); }
  };

  if (!talks.length) return null;
  const unacked = talks.filter(t => !t.acked_at).length;
  return (
    <div className={`rounded-xl border p-3.5 ${unacked ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
      <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5 mb-1.5">
        <span className={`material-symbols-outlined text-lg ${unacked ? 'text-amber-500' : 'text-green-600'}`}>
          {unacked ? 'campaign' : 'verified'}
        </span>
        Toolbox talks
        {unacked > 0 && <span className="text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5">{unacked} to sign</span>}
      </p>
      <div className="space-y-1.5">
        {talks.map(t => (
          <div key={t.id} className="bg-white rounded-lg border border-gray-100">
            <button onClick={() => setOpen(o => o === t.id ? null : t.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left">
              <span className={`material-symbols-outlined text-base ${t.acked_at ? 'text-green-600' : 'text-amber-500'}`}>
                {t.acked_at ? 'check_circle' : 'radio_button_unchecked'}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900 truncate">{t.title}</span>
                <span className="block text-[10px] text-gray-400">
                  {new Date(t.talk_date + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                  {t.acked_at ? ' · signed' : ' · needs your sign-off'}
                </span>
              </span>
            </button>
            {open === t.id && (
              <div className="border-t border-gray-50 px-3 py-2.5">
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{t.body}</p>
                {!t.acked_at && (
                  <button onClick={() => ack(t.id)}
                    className="mt-2 h-9 px-4 rounded-lg bg-[#C77D3F] text-white text-xs font-bold flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-base">draw</span>
                    I've read this — sign off
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}